import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import Button from 'src/components/Button';
import SelectField from 'src/components/SelectField';
import TextField from 'src/components/TextField';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { STUDENT_GRADES, StudentFormData, studentSchema } from './adminSetup.schema';
import { useCreateStudentMutation } from './adminSetupApi';
import { describeApiError } from './describeApiError';

type Props = { onCreated: (studentId: string, label: string) => void };

const GRADE_OPTIONS = STUDENT_GRADES.map((g) => ({ label: g, value: g }));

const StudentForm = ({ onCreated }: Props) => {
  const dispatch = useAppDispatch();
  const [createStudent, { isLoading }] = useCreateStudentMutation();
  const { control, handleSubmit, reset } = useForm<StudentFormData>({
    resolver: zodResolver(studentSchema),
    mode: 'onChange',
    defaultValues: { firstName: '', lastName: '', birthDate: '', nycId: '', gender: '' },
  });

  const submit = handleSubmit(async (data) => {
    try {
      const res = await createStudent({
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate || null,
        nycId: data.nycId || null,
        gender: data.gender || null,
        grade: data.grade,
      }).unwrap();
      dispatch(addToast({ text: 'Student created', type: 'positive' }));
      onCreated(res.id, `${res.firstName} ${res.lastName}`);
      reset();
    } catch (error) {
      dispatch(
        addToast({ text: describeApiError(error, 'Could not create student'), type: 'negative' }),
      );
      console.error('Could not create student', error);
    }
  });

  const text = (name: keyof StudentFormData, label: string, type = 'text') => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          label={label}
          type={type}
          error={error?.message}
          {...field}
          value={field.value ?? ''}
        />
      )}
    />
  );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {text('firstName', 'First name')}
      {text('lastName', 'Last name')}
      <Controller
        name="grade"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <SelectField
            label="Grade"
            placeholder="Pick a grade"
            error={error?.message}
            value={field.value ?? ''}
            onValueChange={field.onChange}
            options={GRADE_OPTIONS}
          />
        )}
      />
      {text('birthDate', 'Date of birth', 'date')}
      {text('nycId', 'NYC ID')}
      {text('gender', 'Gender')}
      <div className="flex items-end">
        <Button type="submit" isLoading={isLoading}>
          Create student
        </Button>
      </div>
    </form>
  );
};

export default StudentForm;
